import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { ApiModule } from '@generated/api.module';
import { definePreset, palette } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { it } from 'primelocale/it.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PRIME_NG_CONFIG, PrimeNG, providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';
import { IamWithPrimeNgModule } from './core/iam-with-primeng/iam-with-primeng.module';
import { CONFIG } from './core/injection-tokens/config.token';
import { LANGUAGE } from './core/injection-tokens/language.token';
import { errorInterceptor } from './core/interceptors/errors.interceptor';
import { forbiddenInterceptor } from './core/interceptors/forbidden.interceptor';
import { unauthorizedInterceptor } from './core/interceptors/unauthorized.interceptor';
import { TenantService } from './core/services/tenant.service';
import { CoreStore } from './core/store/core.store';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([unauthorizedInterceptor, forbiddenInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    importProvidersFrom([
      ApiModule.forRoot({
        rootUrl: '/api',
      }),
      IamWithPrimeNgModule,
    ]),
    providePrimeNG({
      theme: {
        preset: Aura,
      },
    }),
    MessageService,
    ConfirmationService,
    {
      provide: LANGUAGE,
      useFactory: () => {
        const locale = inject(LOCALE_ID);
        const language = locale.substring(0, 2);
        return language === 'it' ? 'it' : 'en';
      },
    },
    {
      provide: PRIME_NG_CONFIG,
      useFactory: () => {
        const language = inject(LANGUAGE);
        const tenant = inject(TenantService).get();
        const AuraCustom = definePreset(Aura, {
          primitive: {
            green: palette('#70B910'),
            red: palette('#FF4E6C'),
            gray: palette('#E2E8F0'),
          },
          semantic: {
            primary: palette(tenant.primaryColor),
          },
        });
        return {
          theme: {
            preset: AuraCustom,
            options: {
              darkModeSelector: 'none',
            },
          },
          ...(language === 'it' ? { translation: it } : {}),
        };
      },
    },
    provideAppInitializer(() => {
      const primeNG = inject(PrimeNG);
      const config = inject(PRIME_NG_CONFIG);
      primeNG.setConfig(config);
    }),
    provideAppInitializer(() => {
      const store = inject(CoreStore);
      const config = inject(CONFIG);
      store.setConfig(config);
    }),
  ],
};
